import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from '../../i18n/I18nContext';

const HOVER_DELAY_MS = 400;

export function ParamTooltip({ paramKey, children }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  const what = t(`match.paramTooltip.${paramKey}.what`);
  const direction = t(`match.paramTooltip.${paramKey}.direction`);
  const range = t(`match.paramTooltip.${paramKey}.range`);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showDelayed = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(true), HOVER_DELAY_MS);
  }, [clearTimer]);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  const toggleMobile = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [open]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  if (!what || what === `match.paramTooltip.${paramKey}.what`) {
    return children;
  }

  return (
    <span
      ref={wrapRef}
      className="match-param-tooltip-wrap"
      onMouseEnter={showDelayed}
      onMouseLeave={hide}
      onFocus={showDelayed}
      onBlur={hide}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen((v) => !v);
        }
      }}
      onClick={toggleMobile}
    >
      {children}
      <span className="match-param-tooltip-icon" aria-hidden>ⓘ</span>
      {open && (
        <span className="match-param-tooltip-panel" role="tooltip">
          <span className="match-param-tooltip-part">{what}</span>
          <span className="match-param-tooltip-part">{direction}</span>
          <span className="match-param-tooltip-part match-param-tooltip-range">{range}</span>
        </span>
      )}
    </span>
  );
}
